/*

 * @FilePath: /code/mall-fe/src/views/web/product/conponents/ProductRate/index.tsx
 * @Description: 商品详情页详情、店铺评价
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Pagination, Avatar } from 'antd';
import { dateFormat } from 'libs/utils';
import Stars from 'comps/Stars';
import styles from './index.module.scss';
import * as ProductService from 'api/product';
import ProductDesc from '../ProductDesc';
import {
  IProductDetail,
  IRate,
  IEvaluation,
  IStarConfig,
  ClickRecommendProduct,
} from '../../product';
import { EVALUATE_OBJECT, EVALUTE_STATUS, PAGE_SIZE_OPT } from '../../constants';
import ProductAttrList from '../../conponents/ProductAttrList';
import RecommendProductListWrapper from './RecommendProductListWrapper';
import { isConcreteProduct } from '../../helper';
import { useForwardRefContext, useShopDataContext } from '../../context';
export const TAB_TYPE = {
  DETAIL: 'detail',
  EVALUATION: 'evaluation',
  QUICK_LIST: 'quickList', // 速采清单
  RECOMMEND_QUICK: 'recommendQuick', // 推荐速采
} as const;

export type TabsType = (typeof TAB_TYPE)[keyof typeof TAB_TYPE];
// 混凝土tab页签
const CONCRETE_PRODUCT_TAB = [TAB_TYPE.DETAIL, TAB_TYPE.EVALUATION, TAB_TYPE.RECOMMEND_QUICK];
// 非混凝土tab页签
const NON_CONCRETE_PRODUCT_TAB = [
  TAB_TYPE.QUICK_LIST,
  TAB_TYPE.DETAIL,
  TAB_TYPE.EVALUATION,
  TAB_TYPE.RECOMMEND_QUICK,
];

const SHOP_STAR_CONFIG: IStarConfig = {
  starSize: 20,
  fontSize: 22,
  starGap: 8,
};
const STAR_CONFIG: IStarConfig = {
  starSize: 15,
  fontSize: 18,
  starGap: 8,
};
type IParams = {
  evaluateObjectType: number;
  status: number;
  evaluateObjectSysNo: string;
  pageSize: number;
  pageNo: number;
};
interface IProps {
  currentTab: TabsType;
  onTabChange: (tab: TabsType) => void;
  currentSelectedRecommendProduct: ClickRecommendProduct; // 当前选中的推荐商品
  evaluateObjectSysNo: string;
  detailContentOfHtml: string;
  shopData: any;
  productDetail: IProductDetail;
}

const ProductAttrListWrapper = ({ productDetail }) => {
  const productMmuListCompRef = useForwardRefContext();
  return (
    <ProductAttrList
      skuDetail={productDetail}
      attributeList={productDetail?.attributeList}
      mmuAttributeList={productDetail?.mmuAttributeList}
      ref={productMmuListCompRef}
    />
  );
};

// 首先定义 tab 的配置，使其更易维护
const TAB_CONFIG = {
  [TAB_TYPE.QUICK_LIST]: {
    key: TAB_TYPE.QUICK_LIST,
    title: '速采清单',
    component: ProductAttrListWrapper,
  },
  [TAB_TYPE.DETAIL]: {
    key: TAB_TYPE.DETAIL,
    title: '商品详情',
    component: ({ attachment, detailContentOfHtml }) => (
      <ProductDesc attachment={attachment} detailContentOfHtml={detailContentOfHtml} />
    ),
  },
  [TAB_TYPE.EVALUATION]: {
    key: TAB_TYPE.EVALUATION,
    title: '评价信息',
    component: ({ evaluationData, getEvaluateData, score }) => (
      <div className={styles.rate_container}>
        {!evaluationData.total ? (
          <div>暂无数据</div>
        ) : (
          <>
            {(!!score || score === 0) && <div className={styles.rate_desc}>
              <span className={styles.rate_title}>店铺评分</span>
              <Stars score={score} custom={SHOP_STAR_CONFIG} isPercent />
            </div>}
            <div className={styles.rate_list_container}>
              {evaluationData.datas.map((item: IRate) => (
                <div className={styles.rate_item} key={item.sysNo}>
                  <div className={styles.rate_avatar}>
                    <Avatar size={40} icon={<i className="iconfont icon-touxiang" />} />
                  </div>
                  <div className={styles.rate_item_org}>{item.organizationName}</div>
                  <div className={styles.comment_detail}>
                    <Stars score={item.totalScore} custom={STAR_CONFIG} />
                    <div className={styles.comment}>{item.comment || '此用户未填写评价内容'}</div>
                    <div className={styles.date}>{dateFormat(item.editDate, 'YYYY-MM-DD')}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.rate_pagenation}>
              <Pagination
                showTotal={(total) => `共${evaluationData.totalPage}页 / ${total}条记录`}
                showQuickJumper
                pageSizeOptions={PAGE_SIZE_OPT}
                current={evaluationData.pageNo}
                pageSize={evaluationData.pageSize}
                total={evaluationData.total}
                onChange={(pageNo, pageSize) => getEvaluateData(pageNo, pageSize)}
              />
            </div>
          </>
        )}
      </div>
    ),
  },
  [TAB_TYPE.RECOMMEND_QUICK]: {
    key: TAB_TYPE.RECOMMEND_QUICK,
    title: '推荐速采',
    component: ({ productDetail, currentSelectedRecommendProduct }) => (
      <RecommendProductListWrapper
        currentSelectedRecommendProduct={currentSelectedRecommendProduct}
      >
        <ProductAttrList
          autoLoad={true}
          currentSelectedRecommendProduct={currentSelectedRecommendProduct}
          skuDetail={productDetail}
          attributeList={productDetail?.attributeList}
          mmuAttributeList={productDetail?.mmuAttributeList}
        />
      </RecommendProductListWrapper>
    ),
  },
};

const ProductRateComp = (props: IProps) => {
  const {
    evaluateObjectSysNo,
    detailContentOfHtml,
    currentTab,
    onTabChange,
    currentSelectedRecommendProduct,
    productDetail,
  } = props;
  const concreteFlag = isConcreteProduct(productDetail);
  // NOTE: 所有非混凝土商品，都具备「速采清单」
  const tabList = concreteFlag ? CONCRETE_PRODUCT_TAB : NON_CONCRETE_PRODUCT_TAB;
  const [evaluationData, setEvaluationData] = useState({} as IEvaluation);
  const { shop } = useShopDataContext();

  const getEvaluateData = useCallback(
    async (pageNo: number = 1, pageSize: number = 10) => {
      try {
        const baseParams = {
          evaluateObjectSysNo: evaluateObjectSysNo,
          evaluateObjectType: EVALUATE_OBJECT.SUPPLIER,
          status: EVALUTE_STATUS.DONE,
        };
        const params: IParams = { ...baseParams, pageSize, pageNo };
        const { data } = await ProductService.getEvaluationData(params);
        if (!data) return;
        setEvaluationData(data as IEvaluation);
      } catch (err) {}
    },
    [evaluateObjectSysNo],
  );

  useEffect(() => {
    if (evaluateObjectSysNo) getEvaluateData();
  }, [evaluateObjectSysNo, getEvaluateData]);

  // 渲染当前选中的 tab 内容
  const renderTabContent = () => {
    return Object.keys(TAB_CONFIG).map((tab) => {
      const TabComponent = TAB_CONFIG[tab].component;
      const componentProps = {
        detailContentOfHtml,
        evaluationData,
        getEvaluateData,
        currentSelectedRecommendProduct,
        productDetail,
        attachment: productDetail?.attachmentList,
        score: shop?.score,
      };
      return (
        <div key={tab} style={{ display: currentTab === tab ? 'block' : 'none' }}>
          <TabComponent {...componentProps} />
        </div>
      );
    });
  };

  return (
    <div className={styles.product_detail_container}>
      <div className={styles.tabs_header}>
        {tabList.map((tab) => (
          <div
            key={tab}
            className={
              currentTab === tab ? styles.tabs_header_item_active : styles.tabs_header_item
            }
            onClick={() => onTabChange(tab)}
          >
            {TAB_CONFIG[tab].title}
            {/* {tab === TAB_TYPE.EVALUATION && evaluationData.total ? (
              <span>({evaluationData.total}条)</span>
            ) : null} */}
          </div>
        ))}
      </div>
      <div className={styles.tabs_content}>{renderTabContent()}</div>
    </div>
  );
};

export default ProductRateComp;
